import { MicProcessor, micProcessingActive } from "@/lib/audio/MicProcessor";
import {
  getMicPreferences,
  micConstraints,
  micProcessingConstraints,
} from "@/lib/audio/micPreferences";
import {
  CameraEffects,
  effectsSupported,
  isCameraEffect,
  type CameraEffect,
} from "@/lib/webrtc/CameraEffects";
import { loadPreferences } from "@/lib/webrtc/DeviceManager";

export type LocalTracks = {
  mic: MediaStreamTrack | null;
  cam: MediaStreamTrack | null;
  screen: MediaStreamTrack | null;
  screenAudio: MediaStreamTrack | null;
};

export class MediaError extends Error {
  readonly reason: "denied" | "missing" | "busy" | "unsupported" | "unknown";
  constructor(reason: MediaError["reason"], message: string) {
    super(message);
    this.name = "MediaError";
    this.reason = reason;
  }
}

function classify(error: unknown): MediaError {
  const name = error instanceof Error ? error.name : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return new MediaError("denied", "Permission to use the device was refused");
    case "NotFoundError":
    case "OverconstrainedError":
      return new MediaError("missing", "No matching device was found");
    case "NotReadableError":
    case "AbortError":
      return new MediaError("busy", "The device is already in use by another application");
    default:
      return new MediaError("unknown", "The device could not be opened");
  }
}

const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
};

export class MediaStreamManager {
  #tracks: LocalTracks = { mic: null, cam: null, screen: null, screenAudio: null };

  #camera: MediaStreamTrack | null = null;

  #microphone: MediaStreamTrack | null = null;
  #processor: MicProcessor | null = null;
  #monitoring = false;
  #effects: CameraEffects | null = null;
  #effect: CameraEffect | null = null;

  #building: Promise<MediaStreamTrack> | null = null;

  get tracks(): Readonly<LocalTracks> {
    return this.#tracks;
  }

  async openMicrophone(deviceId?: string | null): Promise<MediaStreamTrack> {
    const wanted = deviceId ?? loadPreferences().microphoneId;
    const constraints: MediaTrackConstraints = { ...micConstraints(getMicPreferences()) };
    if (wanted !== null && wanted !== undefined) constraints.deviceId = { exact: wanted };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
      const [track] = stream.getAudioTracks();
      if (track === undefined) throw new MediaError("missing", "No microphone track was produced");
      const previous = this.#microphone;
      this.#microphone = track;
      if (previous !== null && previous !== track) previous.stop();
      return this.#publishMicrophone();
    } catch (error) {
      if (
        wanted !== null &&
        wanted !== undefined &&
        error instanceof Error &&
        error.name === "OverconstrainedError"
      ) {
        return this.openMicrophone(null);
      }
      throw classify(error);
    }
  }

  async openCamera(deviceId?: string | null): Promise<MediaStreamTrack> {
    const wanted = deviceId ?? loadPreferences().cameraId;
    const constraints: MediaTrackConstraints = { ...VIDEO_CONSTRAINTS };
    if (wanted !== null && wanted !== undefined) constraints.deviceId = { exact: wanted };
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: constraints });
      const [track] = stream.getVideoTracks();
      if (track === undefined) throw new MediaError("missing", "No camera track was produced");
      const previous = this.#camera;
      this.#camera = track;
      if (previous !== null && previous !== track) previous.stop();
      return await this.#publishCamera();
    } catch (error) {
      if (
        wanted !== null &&
        wanted !== undefined &&
        error instanceof Error &&
        error.name === "OverconstrainedError"
      ) {
        return this.openCamera(null);
      }
      throw classify(error);
    }
  }

  async openScreen(): Promise<{ video: MediaStreamTrack; audio: MediaStreamTrack | null }> {
    if (typeof navigator.mediaDevices?.getDisplayMedia !== "function") {
      throw new MediaError("unsupported", "This browser cannot share a screen");
    }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: true,
      });
      const [video] = stream.getVideoTracks();
      const audio = stream.getAudioTracks()[0] ?? null;
      if (video === undefined) throw new MediaError("missing", "No screen track was produced");
      this.#replace("screen", video);
      this.#replace("screenAudio", audio);
      return { video, audio };
    } catch (error) {
      throw classify(error);
    }
  }

  get microphoneInput(): MediaStreamTrack | null {
    return this.#microphone;
  }

  monitorMicrophone(on: boolean): boolean {
    this.#monitoring = on;
    this.#processor?.monitor(on);
    return this.#processor !== null && on;
  }

  get monitoring(): boolean {
    return this.#monitoring && this.#processor !== null;
  }

  async applyMicSettings(): Promise<MediaStreamTrack | null> {
    const microphone = this.#microphone;
    if (microphone === null) return null;
    const preferences = getMicPreferences();
    try {
      await microphone.applyConstraints(micProcessingConstraints(preferences));
    } catch (error) {
      console.error("microphone constraint update failed:", error);
    }
    return this.#publishMicrophone();
  }

  async setCameraEffect(effect: CameraEffect): Promise<MediaStreamTrack | null> {
    this.#effect = effect;
    if (this.#camera === null) return null;
    return await this.#publishCamera();
  }

  close(kind: keyof LocalTracks): void {
    if (kind === "mic") {
      this.#processor?.stop();
      this.#processor = null;
      this.#monitoring = false;
      const microphone = this.#microphone;
      this.#microphone = null;

      this.#replace("mic", null);
      microphone?.stop();
      return;
    }
    if (kind === "cam") {
      this.#effects?.stop();
      this.#effects = null;
      const camera = this.#camera;
      this.#camera = null;

      this.#replace("cam", null);
      camera?.stop();
      return;
    }
    this.#replace(kind, null);
  }

  closeScreen(): void {
    this.close("screen");
    this.close("screenAudio");
  }

  closeAll(): void {
    for (const kind of ["mic", "cam", "screen", "screenAudio"] as const) this.close(kind);
  }

  #replace(kind: keyof LocalTracks, track: MediaStreamTrack | null): void {
    const previous = this.#tracks[kind];

    if (
      previous !== null &&
      previous !== track &&
      previous !== this.#camera &&
      previous !== this.#microphone
    ) {
      previous.stop();
    }
    this.#tracks = { ...this.#tracks, [kind]: track };
  }

  #publishMicrophone(): MediaStreamTrack {
    const microphone = this.#microphone;
    if (microphone === null) throw new MediaError("missing", "The microphone is not open");

    if (!micProcessingActive(getMicPreferences())) {
      this.#processor?.stop();
      this.#processor = null;
      this.#replace("mic", microphone);
      return microphone;
    }

    try {
      this.#processor ??= new MicProcessor();
      const output = this.#processor.start(microphone, getMicPreferences());

      this.#processor.monitor(this.#monitoring);
      this.#replace("mic", output);
      return output;
    } catch {
      this.#processor?.stop();
      this.#processor = null;
      this.#replace("mic", microphone);
      return microphone;
    }
  }

  async #publishCamera(): Promise<MediaStreamTrack> {
    const queued = (this.#building ?? Promise.resolve(null))
      .catch(() => null)
      .then(async () => await this.#buildCamera());
    this.#building = queued;
    try {
      return await queued;
    } finally {
      if (this.#building === queued) this.#building = null;
    }
  }

  async #buildCamera(): Promise<MediaStreamTrack> {
    const camera = this.#camera;
    if (camera === null) throw new MediaError("missing", "The camera is not open");

    this.#effect ??= remembered();
    const effect = effectsSupported() ? this.#effect : "none";
    if (effect === "none") {
      this.#effects?.stop();
      this.#effects = null;
      this.#replace("cam", camera);
      return camera;
    }

    try {
      this.#effects ??= new CameraEffects();
      const output = await this.#effects.start(camera, effect);
      this.#effects.update(effect);
      this.#replace("cam", output);
      return output;
    } catch {
      this.#effects?.stop();
      this.#effects = null;
      this.#replace("cam", camera);
      return camera;
    }
  }
}

function remembered(): CameraEffect {
  const stored = loadPreferences().cameraEffect;
  return stored !== null && isCameraEffect(stored) ? stored : "none";
}

export function toStream(...tracks: (MediaStreamTrack | null)[]): MediaStream {
  return new MediaStream(tracks.filter((t): t is MediaStreamTrack => t !== null));
}
